import { useEffect, useRef } from 'react';

/**
 * 在表格横向滚动容器内消费触控板横滑，减少误触浏览器「后退/前进」手势。
 *
 * @param {React.RefObject<HTMLElement | null>} scrollContainerRef
 * @param {object} [options]
 * @param {boolean} [options.enabled]
 * @param {number} [options.threshold]
 * @param {number} [options.edgeHoldMs]
 * @param {unknown} [options.watchValue] 变化时重新挂载监听（如表体节点重建）
 */
export function useHorizontalScrollGuard(scrollContainerRef, options = {}) {
    const enabled = options.enabled ?? true;
    const threshold = options.threshold ?? 6;
    const edgeHoldMs = options.edgeHoldMs ?? 220;
    const watchValue = options.watchValue;
    const lastInterceptAtRef = useRef(0);

    useEffect(() => {
        if (!enabled) return;
        void watchValue;

        const el = scrollContainerRef.current;
        if (!el) return;

        const handleWheel = event => {
            const absX = Math.abs(event.deltaX);
            const absY = Math.abs(event.deltaY);
            const isHorizontalPrimary = absX > absY && absX >= threshold;
            if (!isHorizontalPrimary) return;

            const maxScrollLeft = el.scrollWidth - el.clientWidth;
            if (maxScrollLeft <= 0) return;

            const scrollLeft = el.scrollLeft;
            const canScrollLeft = scrollLeft > 0;
            const canScrollRight = scrollLeft < maxScrollLeft - 1;
            const canScrollInDirection = event.deltaX < 0 ? canScrollLeft : canScrollRight;

            const now = performance.now();
            const inEdgeHoldWindow = now - lastInterceptAtRef.current <= edgeHoldMs;
            if (!canScrollInDirection && !inEdgeHoldWindow) {
                return;
            }

            if (event.cancelable) {
                event.preventDefault();
            }

            if (canScrollInDirection) {
                const nextScrollLeft = Math.max(0, Math.min(maxScrollLeft, scrollLeft + event.deltaX));
                el.scrollLeft = nextScrollLeft;
            }

            lastInterceptAtRef.current = now;
        };

        el.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
            el.removeEventListener('wheel', handleWheel);
        };
    }, [enabled, threshold, edgeHoldMs, scrollContainerRef, watchValue]);
}
